// UpdateForm.js
import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, CardBody } from 'reactstrap';
import NameInput from '../../common/NameInput';
import EmailInput from '../../common/EmailInput';
import RadioGroup from '../../common/RadioGroup';
import AlphaNumericInput from '../../common/AlphaNumericInput';
import DropdownInput from '../../common/DropdownInput';
import SingleCheckbox from '../../common/SingleCheckbox'; // Import the SingleCheckbox component
import AmountInput from '../../common/AmountInput';
import DimensionInput from '../../common/DimensionInput';
import WeightInput from '../../common/WeightInput';
import DatePickerInput from '../../common/DatePickerInput';
import PhoneNumberInput from '../../common/PhoneNumberInput';
import UrlInput from '../../common/UrlInput';

const UpdateForm = () => {
    const initialFormData = {
        name: 'John',
        email: 'john.doe@example.com',
        gender: 'female',
        sku: '123ABC',
        unit: 'lb',
        termsAccepted: true, // Initialize terms checkbox field
        amount: 250,
        dimensions: { length: 10, width: 12, height: 14 },
        weight: 22,
        date: new Date(),
        phone: "918855668535",
        url: "www.google.com"
    };

    const [formData, setFormData] = useState(initialFormData);
    const [validateTrigger, setValidateTrigger] = useState(false);

    const genderOptions = [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' },
    ];

    const unitOptions = [
        { value: 'kg', label: 'Kilogram' },
        { value: 'lb', label: 'Pound' },
        { value: 'g', label: 'Gram' },
    ];

    const handleChange = (name, value) => {
        if (name?.startsWith("dimensions.")) {
            const dimensionKey = name.split(".")[1];
            setFormData((prev) => ({
                ...prev,
                dimensions: {
                    ...prev.dimensions,
                    [dimensionKey]: value,
                },
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };


    const handleSubmit = (e) => {
        e.preventDefault();
        setValidateTrigger(true); // Trigger validation on submit

        // Only log the form data if terms are accepted
        if (formData.termsAccepted) {
            console.log("Form Updated:", formData);
        }
    };

    return (
        <div className='page-content components'>
            <Container>
                <Card>
                    <CardBody>
                        <h2 className="mb-4">Update Entry</h2>
                        <Form onSubmit={handleSubmit}>
                            <Row className="mb-4">
                                <Col lg={6}>
                                    <NameInput
                                        name="name"
                                        id="fullNameInput"
                                        value={formData.name}
                                        placeholder="Enter your full name"
                                        required={true}
                                        layout="below"
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        helpText="Please enter your full name."
                                        errorMessage="Name must be at least 3 characters."
                                    />
                                </Col>
                                <Col lg={6}>
                                    <EmailInput
                                        name="email"
                                        id="businessEmailInput"
                                        value={formData.email}
                                        placeholder="Enter your business email"
                                        required={true}
                                        layout="below"
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        helpText="Please enter a valid business email."
                                        requiredErrorMessage="Email is required."
                                        validationErrorMessage="Please enter a valid email address."
                                        businessEmailOnly={true}
                                    />
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col lg={6}>
                                    <AlphaNumericInput
                                        label="SKU"
                                        name="sku"
                                        id="skuInput"
                                        placeholder="Enter SKU"
                                        required={true}
                                        layout="below"
                                        value={formData.sku}
                                        onChange={(e) => handleChange('sku', e.target.value)}
                                        helpText="Please enter a SKU."
                                        validateTrigger={validateTrigger}
                                    />
                                </Col>
                                <Col lg={6}>
                                    <DropdownInput
                                        label="Select Dropdown Option"
                                        name="unit"
                                        id="unit"
                                        options={unitOptions}
                                        required={true}
                                        validateTrigger={validateTrigger}
                                        requiredErrorMessage="Please select an option."
                                        onChange={(option) => handleChange('unit', option ? option.value : '')}
                                        onBlur={() => setValidateTrigger(true)}
                                        defaultSelectedKey={formData.unit}
                                        placeholder="Please select an option"
                                    />
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col lg={6}>
                                    <RadioGroup
                                        label="Gender"
                                        options={genderOptions}
                                        required={true}
                                        value={formData.gender}
                                        validateTrigger={validateTrigger}
                                        requiredErrorMessage="Please select a gender."
                                        onChange={(value) => handleChange('gender', value)}
                                        helpText="Please select your gender."
                                    />
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col lg={6}>
                                    <SingleCheckbox
                                        label="I accept the terms and conditions"
                                        name="termsAccepted"
                                        required={true}
                                        validateTrigger={validateTrigger}
                                        onChange={(value) => handleChange('termsAccepted', value)}
                                        checked={formData.termsAccepted}
                                    />
                                </Col>

                            </Row>
                            <Row className="mb-4">
                                <Col lg={6}>
                                    <AmountInput
                                        label="Amount"
                                        name="amount"
                                        value={formData.amount}
                                        required={true}
                                        min={1}
                                        max={100000}
                                        validateTrigger={validateTrigger}
                                        onChange={(name, value) => handleChange(name, value)}
                                    />
                                </Col>
                                <Col lg={6}>
                                    <UrlInput
                                        name="url"
                                        id="url"
                                        label="Website URL"
                                        required={true}
                                        placeholder="Enter website URL"
                                        validateTrigger={validateTrigger}
                                        onChange={(value) => handleChange('url', value)}
                                        onBlur={(e) => handleChange("url", e.target.value)}
                                        value={formData.url}
                                        helpText="Please provide a valid website URL."
                                        requiredErrorMessage="URL is required."
                                        validationErrorMessage="Please enter a valid URL."
                                    />
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col lg={6}>
                                    <DimensionInput
                                        label="Dimensions"
                                        name="dimensions" // use name prefix for nested fields
                                        value={formData.dimensions} // Pass the initial dimensions
                                        required={true}
                                        minLength={1}
                                        maxLength={500}
                                        onChange={(value) => handleChange('dimensions', value)}
                                        helpText="Enter length, width, and height."
                                        validateTrigger={validateTrigger}
                                        defaultUnit="m"
                                    />
                                </Col>
                                <Col lg={6}>
                                    <WeightInput
                                        label="Weight"
                                        name="weight"
                                        required={true}
                                        minValue={1}
                                        maxValue={200}
                                        unitOptions={unitOptions.map(option => option.value)} // Pass unit options
                                        defaultUnit={formData.unit} // Default selected unit from formData
                                        placeholder="Enter weight"
                                        validateTrigger={validateTrigger}
                                        onChange={(value) => handleChange('weight', value)}
                                        onBlur={(e) => handleChange("weight", e.target.value)} // Ensure onBlur calls handleChange
                                        value={formData.weight} // Set default value for weight
                                    />
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col lg={6}>
                                    <DatePickerInput
                                        name="date"
                                        label="Select Date"
                                        value={formData.date}
                                        required={true}
                                        onChange={(date) => handleChange('date', date)}
                                        dateRange={{
                                            minDate: new Date(), // Example: Min date today
                                            maxDate: new Date('2025-12-31') // Example: Max date set to the end of 2025
                                        }}
                                    />
                                </Col>
                                <Col lg={6}>
                                    <PhoneNumberInput
                                        label="Phone Number"
                                        name="phone"
                                        value={formData.phone}
                                        required={true}
                                        validateTrigger={validateTrigger}
                                        onChange={(value) => handleChange('phone', value)}
                                        onBlur={(e) => handleChange("phone", e.target.value)}
                                    />
                                </Col>

                            </Row>
                            <Button type="submit" color="primary">
                                Update Entry
                            </Button>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default UpdateForm;
